/**
 * Pure presentation component for the save confirmation dialog shown
 * when the user clicks Save with unresolved conflicts present.
 *
 * Story 2.5 acceptance criteria require an additional confirmation
 * when the extraction still has categories with `isConflict: true`
 * items (i.e. the user hasn't yet clicked "Mark Conflicts Resolved"
 * via SE-4342's per-category action). The dialog lists the specific
 * categories that still have unresolved conflicts so the admin can
 * decide whether to proceed or cancel and finish resolving first.
 *
 * Renders through the shared ConfirmDialog molecule (SE-5524;
 * adopted here in SE-8567 / DES-44). The `save-confirm-cancel-btn`,
 * `save-confirm-btn` and `unresolved-conflicts-message` testIDs are kept.
 * @module features/contracts/components/SaveExtractionDialog/SaveExtractionDialogView
 */

import { AlertTriangle } from "lucide-react-native";
import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Text } from "@/components/atoms/Text";
import ConfirmDialog from "@/components/molecules/ConfirmDialog";

/** Props for {@link SaveExtractionDialogView}. */
interface Props {
  /** Whether the dialog is currently visible. */
  readonly isOpen: boolean;
  /** Whether a save mutation is currently in flight. */
  readonly isSaving: boolean;
  /**
   * Display-formatted names of categories that still have unresolved
   * conflicts. Shown in the dialog body so the admin sees which
   * categories still need review before saving.
   */
  readonly unresolvedCategoryNames: readonly string[];
  /** Invoked when the user taps Cancel or dismisses the dialog. */
  readonly onClose: () => void;
  /** Invoked when the user taps the Save Anyway button. */
  readonly onConfirm: () => void;
}

/**
 * Joins an array of category names into a prose fragment suitable for
 * embedding in the dialog body (e.g. "Fixed Terms", "Clauses, Other",
 * or "Fixed Terms, Clauses, and Other").
 * @param names - Display category names
 * @returns Prose fragment; empty string when the array is empty
 */
function formatCategoryList(names: readonly string[]): string {
  if (names.length === 0) return "";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
}

/**
 * Confirmation dialog for saving an extraction that still has
 * unresolved conflicts. The body enumerates the specific unresolved
 * categories so the admin knows exactly what they're overriding.
 * @param props - View props
 * @param props.isOpen - Whether the dialog is visible
 * @param props.isSaving - Whether the save mutation is in flight
 * @param props.unresolvedCategoryNames - Display category names with unresolved conflicts
 * @param props.onClose - Invoked when the user cancels or dismisses
 * @param props.onConfirm - Invoked when the user confirms the save
 * @returns The rendered alert dialog
 */
const SaveExtractionDialogView = ({
  isOpen,
  isSaving,
  unresolvedCategoryNames,
  onClose,
  onConfirm,
}: Props) => (
  <ConfirmDialog
    isOpen={isOpen}
    onClose={onClose}
    onConfirm={onConfirm}
    title="Save with unresolved conflicts?"
    // The copy renders in the slot, not as a body line, so it keeps the
    // `unresolved-conflicts-message` wrapper, with its original styling.
    bodySlot={
      <Box testID="unresolved-conflicts-message">
        <Text
          variant="unstyled"
          className="text-center text-title-sm text-content-secondary"
        >
          There are unresolved conflicts in{" "}
          {formatCategoryList(unresolvedCategoryNames)}. Are you sure you want
          to save?
        </Text>
      </Box>
    }
    icon={AlertTriangle}
    tone="warning"
    cancelLabel="Cancel"
    confirmLabel={isSaving ? "Saving..." : "Save Anyway"}
    isConfirming={isSaving}
    cancelTestID="save-confirm-cancel-btn"
    confirmTestID="save-confirm-btn"
  />
);

SaveExtractionDialogView.displayName = "SaveExtractionDialogView";

export default memo(SaveExtractionDialogView);
